import React, { useState, useEffect, useRef } from "react";
// utils
import formatMoney from "lib/formatMoney";
// mui
import { useTheme } from "@mui/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import StackItem from "components/Loans/StackItem";
import Box from "@mui/material/Box";

// const bridgeInterestRate = 12;
const bridgeInterestRate = 10.25;

export default function FinalQuoteDisplay({
  loanQuoteData,
  answers,
  processQuote,
}) {
  const theme = useTheme();
  const [loanType, setLoanType] = useState("");
  const [outputLtvRanges, setOutputLtvRanges] = useState("");
  const [rows, setRows] = useState("");
  const [portfolioLoan, setPortfolioLoan] = useState("");
  const [loanPurpose, setLoanPurpose] = useState("");
  const [creditScore, setCreditScore] = useState("");

  useEffect(() => {
    const loanType = loanQuoteData.loanType;
    setLoanType(loanType);

    switch (loanType) {
      case "DSCR":
        const portfolioLoan = answers.filter((answer) => {
          return answer.q_title === "Portfolio loan";
        })?.[0].a_string;
        // Determine what to use as the base
        // for property value
        // purchase price vs as-is value for refinance vs purchase
        const loanPurpose = answers?.filter((answer) => {
          return answer.q_title === "Loan purpose";
        })?.[0]?.a_string;

        const creditScore = answers?.filter((answer) => {
          return answer.q_title === "Credit score";
        })?.[0]?.a_string;
        setPortfolioLoan(portfolioLoan);
        setLoanPurpose(loanPurpose);
        setCreditScore(creditScore);

        let ltvRanges;

        ltvRanges = [
          { title: "0-5", maxValue: 5 },
          { title: "5-10", maxValue: 10 },
          { title: "10-15", maxValue: 15 },
          { title: "15-20", maxValue: 20 },
          { title: "20-25", maxValue: 25 },
          { title: "25-30", maxValue: 30 },
          { title: "30-35", maxValue: 35 },
          { title: "35-40", maxValue: 40 },
          { title: "40-45", maxValue: 45 },
          { title: "45-50", maxValue: 50 },
          { title: "50-55", maxValue: 55 },
          { title: "55-60", maxValue: 60 },
        ];

        switch (loanPurpose) {
          case "Refinance":
            if (creditScore < 700) {
              // 680 - 699	65%
              ltvRanges.push({ title: "61-65", maxValue: 65 });
            } else if (creditScore < 720) {
              // 700 - 719	70%
              ltvRanges.push({ title: "61-65", maxValue: 65 });
              ltvRanges.push({ title: "66-70", maxValue: 70 });
            } else {
              // 720+	75%
              ltvRanges.push({ title: "61-65", maxValue: 65 });
              ltvRanges.push({ title: "66-70", maxValue: 70 });
              ltvRanges.push({ title: "71-75", maxValue: 75 });
            }

            break;
          case "Purchase":
            if (creditScore < 700) {
              // 680 - 699	70%
              ltvRanges.push({ title: "61-65", maxValue: 65 });
              ltvRanges.push({ title: "66-70", maxValue: 70 });
            } else if (creditScore < 720) {
              // 700 - 719	75%
              ltvRanges.push({ title: "61-65", maxValue: 65 });
              ltvRanges.push({ title: "66-70", maxValue: 70 });
              ltvRanges.push({ title: "71-75", maxValue: 75 });
            } else {
              // 720+	80%
              ltvRanges.push({ title: "61-65", maxValue: 65 });
              ltvRanges.push({ title: "66-70", maxValue: 70 });
              ltvRanges.push({ title: "71-75", maxValue: 75 });
              ltvRanges.push({ title: "76-80", maxValue: 80 });
            }

            break;
        }

        let rows = [
          // { name: "DSCR" },
          { name: "Loan amount" },
          // { name: "Origination fee" }, // NOTE: agreed to remove this for now to encourage click through
          // { name: "Monthly payment" },
          { name: "Interest rate" },
        ];

        let outputLtvRanges = [];
        // build up rows object with incoming data

        // Get all numeric LTV keys and find the highest one
        const ltvKeys = Object.keys(loanQuoteData)
          .filter((key) => !isNaN(parseInt(key)))
          .map((key) => parseInt(key));
        const highestLtv = Math.max(...ltvKeys);

        // Add TBD option if highest LTV is below 65
        let shouldAddTbdOption = highestLtv < 65;
        if (shouldAddTbdOption) {
          // Add TBD option to loanQuoteData
          loanQuoteData["TBD"] = {
            loanAmount: "TBD",
            loanInterestRate: "TBD",
            DSCR: "TBD",
            mortgagePmt: "TBD",
          };
        }

        Object.keys(loanQuoteData).forEach((key) => {
          if (!isNaN(parseInt(key)) || key === "TBD") {
            // if a number or TBD

            const maxValue = key;

            outputLtvRanges.push({ maxValue: key });

            rows.forEach((row) => {
              switch (row.name) {
                case "DSCR":
                  row[maxValue] =
                    maxValue === "TBD"
                      ? "TBD"
                      : `${
                          loanQuoteData[maxValue]["DSCR"] &&
                          parseFloat(loanQuoteData[maxValue]["DSCR"]).toFixed(1)
                        }`;
                  break;
                case "Loan amount":
                  row[maxValue] =
                    maxValue === "TBD"
                      ? "TBD"
                      : `$${formatMoney(
                          loanQuoteData[maxValue]["loanAmount"],
                          0
                        )}`;
                  break;
                case "Interest rate":
                  row[maxValue] =
                    maxValue === "TBD"
                      ? "TBD"
                      : ["DSCR too low", "Below min loan"].includes(
                          loanQuoteData[maxValue]["loanInterestRate"]
                        )
                      ? loanQuoteData[maxValue]["loanInterestRate"]
                      : `${
                          Math.floor(
                            loanQuoteData[maxValue]["loanInterestRate"] * 100
                          ) / 100
                        }%`;

                  break;
                case "Monthly payment":
                  row[maxValue] =
                    maxValue === "TBD"
                      ? "TBD"
                      : `$${formatMoney(
                          loanQuoteData[maxValue]["mortgagePmt"],
                          0
                        )}`;
                  break;
                case "Origination fee":
                  if (maxValue === "TBD") {
                    row[maxValue] = "TBD";
                  } else {
                    let fee = loanQuoteData[maxValue]["loanAmount"] * 0.02;
                    if (fee < 2000) {
                      fee = 2000;
                    }
                    row[maxValue] = `$${formatMoney(fee, 0)}`;
                  }
                  break;
              }
            });
          }
        });

        setRows(rows);

        // undoing this because at some point this was requested
        // so flipping it back
        outputLtvRanges = outputLtvRanges.reverse();

        setOutputLtvRanges(outputLtvRanges);

        break;
    }
  }, [loanQuoteData]);

  return [
    (loanType === "Fix and flip" || loanType === "Ground up construction") && (
      <Box>
        <Stack>
          <StackItem>
            Interest rate: <b>{bridgeInterestRate}%</b>
          </StackItem>
          <StackItem>
            Loan amount: <b>${formatMoney(loanQuoteData.loanAmount, 0)}</b>
          </StackItem>
          <StackItem
            sx={{
              fontSize: theme.spacing(2.5),
            }}
          >
            Press SELECT to continue to the term sheet and pre-approval
          </StackItem>
        </Stack>
        <Button
          sx={{
            marginTop: theme.spacing(2),
            color: "white !important",
            "&:hover, :focus": {
              color: `${theme.palette.primary.contrastColor} !important`,
              backgroundColor: `${theme.palette.success.dark} !important`,
            },
          }}
          variant="contained"
          onClick={() => {
            const rehabComponent = answers?.filter((question) => {
              return question.q_title === "Fix and flip loan amount";
            })[0]?.a_string[1];

            const purchasePrice = answers.filter((question) => {
              return question.q_title === "Purchase price";
            })[0]?.a_string;

            const ltfc =
              (
                parseInt(loanQuoteData.loanAmount) /
                (parseInt(purchasePrice) + parseInt(rehabComponent))
              ).toFixed(2) * 100;

            // NOTE: SelectedLoan specification
            // for bridge
            processQuote({
              // loanType: loanQuoteData.loanType,
              // portfolio: loanQuoteData.portfolio,
              // loanAmount: loanQuoteData.loanAmount,
              // mortgagePmt: loanQuoteData.mortgagePmt,
              // originationFee: loanQuoteData.originationFee,
              ...loanQuoteData,
              loanInterestRate: bridgeInterestRate,
              ltfc: ltfc, // for bridge up to 90 of purchase / 100 of rehab
            });
          }}
        >
          Select
        </Button>
      </Box>
    ),
    loanType === "DSCR" && (
      <TableContainer component={Paper}>
        <Table size="small" aria-label="a dense table">
          <TableHead sx={{}}>
            <TableRow>
              <TableCell
                sx={{
                  backgroundColor: theme.palette.primary.main,
                  fontWeight: 600,
                  color: "white !important",
                }}
              >
                LTV
              </TableCell>
              {outputLtvRanges.map((LTV) => {
                return (
                  <TableCell
                    sx={{
                      backgroundColor: theme.palette.primary.main,
                      fontWeight: 600,
                      color: "white !important",
                    }}
                    align="center"
                  >
                    {LTV.maxValue === "TBD"
                      ? "Not seeing LTV you expected?"
                      : `${LTV.maxValue}%`}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow
                key={row.name}
                sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
              >
                <TableCell component="th" scope="row">
                  {row.name}
                </TableCell>
                {outputLtvRanges.map((ltvRange) => {
                  const ltvMaxValue = ltvRange.maxValue;

                  return (
                    <TableCell
                      sx={{ textAlign: "center" }}
                      component="th"
                      scope="row"
                    >
                      {row[ltvMaxValue]}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}

            <TableRow
              key={"loan_ltv_picker_row"}
              sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
            >
              <TableCell component="th" scope="row">
                Choose your loan
              </TableCell>
              {outputLtvRanges.map((ltvRange) => {
                const ltvMaxValue = ltvRange.maxValue;

                const [interestRateRow] = rows.filter((row) => {
                  return row.name === "Interest rate";
                });
                const [loanAmountRow] = rows.filter((row) => {
                  return row.name === "Loan amount";
                });
                const [monthlyPaymentRow] = rows.filter((row) => {
                  return row.name === "Monthly payment";
                });
                const [originationFeeRow] = rows.filter((row) => {
                  return row.name === "Origination fee";
                });

                return (
                  <TableCell
                    sx={{ textAlign: "center" }}
                    component="th"
                    scope="row"
                  >
                    {(!["Below min loan", "DSCR too low"].includes(
                      //this works for now since we only have 1 product
                      interestRateRow[ltvMaxValue]
                    ) ||
                      ltvMaxValue === "TBD") && (
                      <Button
                        sx={{
                          color: "white !important",
                          "&:hover, :focus": {
                            color: `${theme.palette.primary.contrastColor} !important`,
                            backgroundColor: `${theme.palette.success.dark} !important`,
                          },
                        }}
                        variant="contained"
                        onClick={() => {
                          // NOTE: selectedLoan specification
                          // for rental

                          const toSaveObj = {
                            loanPurpose: loanPurpose,
                            ltv: ltvMaxValue,
                            portfolio: portfolioLoan === "Yes",

                            ...loanQuoteData[ltvMaxValue],
                          };

                          processQuote(toSaveObj);
                        }}
                      >
                        Select
                      </Button>
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    ),
    loanType === "Slow flip" &&
      (loanQuoteData.loanAmount === "Loan amount too low" ? (
        <Box>
          Sorry we can't quote this loan. Our minimum amount is $25,000 and this
          property can't support debt of this size.
        </Box>
      ) : (
        <Box>
          <Stack>
            <StackItem>
              Interest rate: <b>15%</b>
            </StackItem>
            <StackItem>
              Term: <b>5 years (fully amortizing)</b>
            </StackItem>
            <StackItem>
              Monthly payment:{" "}
              <b>${formatMoney(loanQuoteData.mortgagePmt, 0)}</b>
            </StackItem>
            <StackItem>
              Loan amount: <b>${formatMoney(loanQuoteData.loanAmount, 0)}</b>
            </StackItem>
            <StackItem
              sx={{
                fontSize: theme.spacing(2.5),
              }}
            >
              Press SELECT to continue to a detailed term sheet
            </StackItem>
          </Stack>
          <Button
            sx={{ marginTop: theme.spacing(2) }}
            variant="contained"
            onClick={() => {
              // NOTE: SelectedLoan specification
              // for bridge

              processQuote(loanQuoteData);
            }}
          >
            Select
          </Button>
        </Box>
      )),
  ];
}
